import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, MessageSquare, Send, Loader2, Clock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor completa los campos requeridos");
      return;
    }

    setLoading(true);
    
    // Simular envío (en producción conectaría con backend/email service)
    setTimeout(() => {
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      setLoading(false);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email",
      value: "soporte@ciafactura.com",
      link: "mailto:soporte@ciafactura.com"
    },
    {
      icon: <Phone className="w-5 h-5" />,
      title: "Teléfono",
      value: "+52 55 1234 5678",
      link: "tel:+525512345678"
    },
    {
      icon: <MessageSquare className="w-5 h-5" />,
      title: "WhatsApp",
      value: "+52 55 1234 5678",
      link: "https://wa.me/525512345678"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Horario",
      value: "Lun - Vie: 9:00 - 18:00",
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura℗" className="h-10 w-auto" />
              <span className="font-bold text-xl text-slate-900">CIA Factura℗</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#5B9BD5] to-[#4A89C4] py-16">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contáctanos</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            ¿Tienes dudas sobre nuestros planes o necesitas ayuda con tu facturación? 
            Estamos aquí para ayudarte.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Información de Contacto</h2>
            
            {contactInfo.map((item, index) => (
              <Card key={index} className="cia-card hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#5B9BD5]/10 flex items-center justify-center text-[#5B9BD5]">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{item.title}</p>
                    {item.link ? (
                      <a 
                        href={item.link} 
                        className="text-slate-900 font-medium hover:text-[#5B9BD5] transition-colors"
                        target={item.link.startsWith("http") ? "_blank" : undefined}
                        rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-slate-900 font-medium">{item.value}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Map placeholder */}
            <Card className="cia-card overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-slate-200 h-48 flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Ciudad de México, México</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="cia-card">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Envíanos un mensaje</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre completo *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Juan Pérez"
                        className="cia-input"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Correo electrónico *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="juan@empresa.com"
                        className="cia-input"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono (opcional)</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+52 55 1234 5678"
                        className="cia-input"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Asunto</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Consulta sobre planes"
                        className="cia-input"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mensaje *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Escribe tu mensaje aquí..."
                      className="cia-input min-h-[150px]"
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="cia-btn-primary w-full md:w-auto px-8"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Mensaje
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* FAQ Preview */}
            <Card className="cia-card mt-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-slate-900 mb-4">Preguntas Frecuentes</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-slate-800">¿Cuánto tiempo tarda el timbrado?</p>
                    <p className="text-sm text-slate-600">El timbrado de CFDI es instantáneo, en cuestión de segundos recibes tu factura con folio fiscal.</p>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">¿Puedo cancelar mi suscripción?</p>
                    <p className="text-sm text-slate-600">Sí, puedes cancelar en cualquier momento sin penalizaciones desde tu panel de control.</p>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">¿Necesito cargar mi certificado CSD?</p>
                    <p className="text-sm text-slate-600">Sí, para emitir CFDI válidos necesitas tus archivos .cer y .key del SAT.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura℗" className="h-8 w-auto" />
              <span className="font-bold">CIA Factura℗</span>
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
              <Link to="/terminos" className="hover:text-white transition-colors">Términos</Link>
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} CIA Factura℗
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
